import { useMemo } from 'react';
import { hackXHR, hackFetch, HEADER_IGNORE_XHR } from './hackHttp';
import { 
  SimpleMetricsExecutor,
  SimpleMetricsReporter,
  type ISimpleMonitorConfig,
  type IEnhancedElementConfig,
  SIMPLE_DEFAULT_CONFIG
} from '../metricsEmptyMonitor/simple';
let isInit: boolean = false;  // 是否初始化
let clickTime: number = 0;
const reportTimer: any = {};
const reportDelay = 800; // 上报延迟时间
// const reportLimit = 10 * 1000; // 上报最大时间，超过这个时间不上报

type ReportingType = {
  reportId: string | number;
  startTime: number;
  reportUrl: string;
  ajaxList: Array<string>;
}

const defaultReport = {
  reportId: '',
  startTime: 0,
  reportUrl: '',
  ajaxList: []
};

let currentReporting: ReportingType = {...defaultReport};

// =============================================================================
// DOM指标监控相关变量（简化版）
// =============================================================================
let metricsExecutor: SimpleMetricsExecutor | null = null;
let metricsReporter: SimpleMetricsReporter | null = null;
let isMetricsMonitorEnabled = false;

const handleReport = (reportId: number | string, startTime: number, endTime: number) => {
  reportTimer[reportId] = setTimeout(() => {
    const { reportId: curReportId, reportUrl } = currentReporting;
    if (reportId === curReportId) {
      const duration = endTime - startTime;
      (window as any)?.aegis?.reportTime({
        name: reportUrl, // 自定义测速名称
        duration, // 自定义测速耗时(0 - 60000)
      });
      currentReporting = {...defaultReport};
      // console.log('handleReport', reportId, duration, startTime, endTime);
    }
  }, reportDelay);
};

const handleAjaxStart = () => {
  const { reportId } = currentReporting;
  if (!reportId) {
    return;
  }
  if (reportTimer[reportId]) {
    clearTimeout(reportTimer[reportId]);
  }
  
  // 停止指标检测
  stopSimpleMetricsDetection();
};

// =============================================================================
// DOM指标监控函数
// =============================================================================

/**
 * 初始化简化DOM指标监控
 */
const initSimpleMetricsMonitor = (pathname: string) => {
  try {
    // 使用简化的默认配置
    const config: ISimpleMonitorConfig = {
      ...SIMPLE_DEFAULT_CONFIG,
      enabled: true
    };
    
    // 创建检测执行器
    if (!metricsExecutor) {
      metricsExecutor = new SimpleMetricsExecutor(config);
      
      // 设置检测结果回调
      metricsExecutor.onDetection = (emptyElements, statistics) => {
        if (emptyElements.length > 0) {
          handleEmptyMetricsDetected(emptyElements, statistics);
        }
      };
    } else {
      metricsExecutor.updateConfig(config);
    }
    
    // 创建数据上报器
    if (!metricsReporter) {
      metricsReporter = new SimpleMetricsReporter(config.reportInterval);
    } else {
      metricsReporter.updateReportInterval(config.reportInterval);
    }
    
    isMetricsMonitorEnabled = true;
    
    console.log(`[SimpleMetricsMonitor] 监控已初始化，页面: ${pathname}`);
    
  } catch (error) {
    console.error(`[SimpleMetricsMonitor] 监控初始化失败:`, error);
    isMetricsMonitorEnabled = false;
  }
};

/**
 * 处理检测到的空指标（简化版）
 */
const handleEmptyMetricsDetected = (emptyElements: IEnhancedElementConfig[], statistics: {
  totalElements: number;
  emptyElements: number;
  emptyRatio: number;
}) => {
  if (!metricsReporter || emptyElements.length === 0) {
    return;
  }
  
  try {
    const { reportUrl, startTime } = currentReporting;
    
    // 构建上下文信息
    const context = {
      pageUrl: window.location.href,
      pagePath: reportUrl || window.location.pathname,
      pageTitle: document.title
    };
    
    // 上报空指标（包含统计信息）
    metricsReporter.reportEmptyMetrics(emptyElements, statistics, context);
    
    // const emptyPercentage = `${(statistics.emptyRatio * 100).toFixed(1)}%`;
    // console.log(`[SimpleMetricsMonitor] 已上报空指标: ${statistics.emptyElements}/${statistics.totalElements}个 (${emptyPercentage})`);
    
  } catch (error) {
    console.error(`[SimpleMetricsMonitor] 空指标处理失败:`, error);
  }
};

/**
 * 开始指标检测（简化版）
 */
const startSimpleMetricsDetection = () => {
  if (!isMetricsMonitorEnabled || !metricsExecutor) {
    return;
  }
  
  try {
    metricsExecutor.start();
    // console.log(`[SimpleMetricsMonitor] 检测已启动`);
  } catch (error) {
    console.error(`[SimpleMetricsMonitor] 启动检测失败:`, error);
  }
};

/**
 * 停止指标检测（简化版）
 */
const stopSimpleMetricsDetection = () => {
  if (metricsExecutor) {
    metricsExecutor.stop();
    // console.log(`[SimpleMetricsMonitor] 检测已停止`);
  }
};

const listenAjaxAndDomChange = () => {
  const { reportId, ajaxList, startTime } = currentReporting;
  if (!reportId) {
    return;
  }
  if (reportTimer[reportId]) {
    clearTimeout(reportTimer[reportId]);
  }

  // ajax请求是否完成
  if (ajaxList.length > 0) {
    return;
  }

  const endTime = Date.now();
  // todo 开始时间比结束时间大，直接return，是否需要判定？
  if (startTime >= endTime) {
    return;
  }

  // 鼠标点击时间比起始时间大，表示路由切换后，有鼠标点击操作
  if (clickTime > startTime) {
    return;
  }

  // 执行页面加载性能上报
  handleReport(reportId, startTime, endTime);
  
  // 在页面加载完成后，开始指标检测
  startSimpleMetricsDetection();
};

export const useReport = (location: Location) => {
  useMemo(() => {
    const { pathname } = location;
    // 事件注册
    if (!isInit) {
      isInit = true;
      // dom监听
      const observer = new MutationObserver((mutationsList) => {
        if (mutationsList.length > 2) {
          listenAjaxAndDomChange();
        }
      });
      //  we track only changes to nodes/DOM elements, not attributes or characterData
      observer.observe(document, { childList: true, subtree: true });

      // ajax监听
      hackXHR({
        send(xhr) {
          // @ts-ignore
          const url = xhr.aegisUrl;
          const report = currentReporting;

          // 当requestHeaderKeys里存在IGNORE_HEADER_LIST的值，不进行ajax监听
          const isIgnoreXhrHeader = xhr._requestHeaderKeys?.includes(HEADER_IGNORE_XHR);
          if (url && report.reportId && !isIgnoreXhrHeader) {
            handleAjaxStart();
            report.ajaxList.push(url);
            xhr.addEventListener('loadend', () => {
              if (currentReporting.reportId && report.reportId === currentReporting.reportId) {
                currentReporting.ajaxList.splice(currentReporting.ajaxList.indexOf(url), 1);
                listenAjaxAndDomChange();
              }
            });
          }
        }
      });

      // fetch监听
      hackFetch({
        beforeFetch(url, fetchOption) {
          const { reportId } = currentReporting;
          if (reportId) {
            (fetchOption as any)._reportId = reportId;
            currentReporting.ajaxList.push(url);
            handleAjaxStart();
          }
        },
        then(res, url, fetchOption) {
          const { _reportId: reportId } = fetchOption as any;
          if (reportId === currentReporting.reportId) {
            currentReporting.ajaxList.splice(currentReporting.ajaxList.indexOf(url), 1);
            listenAjaxAndDomChange();
          }
        },
        catch(err, url, fetchOption) {
          const { _reportId: reportId } = fetchOption as any;
          if (reportId === currentReporting.reportId) {
            currentReporting.ajaxList.splice(currentReporting.ajaxList.indexOf(url), 1);
            listenAjaxAndDomChange();
          }
        }
      });

      // 点击监听
      window.addEventListener('click', (e) => {
        // 判定是用户点击还是js触发
        if (e?.isTrusted === false) {
          return;
        }
        clickTime = Date.now();
      });
    }

    const now = Date.now();
    // const { reportId, startTime } = currentReporting;
    currentReporting = {
      reportId: now,
      reportUrl: pathname,
      startTime: now,
      ajaxList: []
    };
    
    // 初始化简化DOM指标监控
    initSimpleMetricsMonitor(pathname);
  }, [location]);
};
